// Source: exams ui/primitives.tsx Spinner one-liner; decisions: added role="status" + aria-label so the spinner is announced.
//
// THE RAMP (2026-09-17, audit C-11): Spectrum's progress circle comes in
// exactly three sizes — --spectrum-progress-circle-size-small: 16px / -medium:
// 32px / -large: 64px, stroke 2 / 3 / 4px (@spectrum-css/tokens v15.2.0,
// .spectrum--medium, the desktop scale) — and this component now names them
// `s` / `m` / `l`. Ten sites in three modules had pasted `loading
// loading-spinner loading-{xs,sm,lg}` because the only size was the baked
// loading-xs, and half of them lost the role and label on the way; daisyUI's
// 20 / 24 / 28px middle rungs (loading-sm/-md/-lg) sit on no Spectrum rung and
// are not reachable here.
//
//   s   loading-xs   16px — daisyUI's mask (viewBox 24, stroke 3) scales its
//                    stroke to exactly --spectrum-progress-circle-thickness-
//                    small = 2px. Override-safe: daisyUI emits .loading-xs
//                    before the larger rungs at equal specificity.
//   m   loading-xl   32px — the same scaling gives a 4px stroke where Spectrum's
//                    -medium is 3px; spectrum.css §11 re-masks
//                    `[data-size="m"]` with stroke 2.25 so it lands on 3px.
//   l   size-16      64px — no daisyUI rung at all; the utility outranks
//                    .loading's width (utilities sort after daisyUI's sublayer),
//                    and §11 re-masks `[data-size="l"]` with stroke 1.5 → 4px.
//
// INLINE OR STANDALONE — two duties, two a11y contracts (the C-11 half):
//   standalone   the spinner is the whole message ("this region is busy"):
//                role="status" + aria-label so the wait is announced.
//   inline       the spinner sits BESIDE its own visible words ("Uploading
//                cover…" with role="status" on the line, a button's pending
//                label): decorative, aria-hidden — a second live region would
//                announce the same wait twice.
//
// COLOUR: the accent by default (progress is accent-only, DEV-GUIDE-002
// §12.3). A `text-*` token in `class` replaces it — the chrome-over-video
// cases pass text-chrome-content — rather than fighting it at equal
// specificity, where the stylesheet's order would decide.
/** Spectrum's three progress-circle sizes: 16 / 32 / 64px. */
export type SpinnerSize = 's' | 'm' | 'l';

/* Complete class strings per size — Tailwind must see each literal. */
const SIZE: Record<SpinnerSize, string> = {
  s: 'loading-xs', // 16px — progress-circle-size-small
  m: 'loading-xl', // 32px — progress-circle-size-medium
  l: 'size-16', // 64px — progress-circle-size-large (no daisyUI rung)
};

const hasInk = (cls: string | undefined): boolean => /(^|\s)text-/.test(cls ?? '');

export function Spinner(props: {
  /** `s` 16px (the inline rung, default), `m` 32px (a panel or tile busy), `l` 64px (a page-blocking wait — a camera starting, a decoder loading). */
  size?: SpinnerSize;
  /** The spinner sits beside its own visible words (a status line, a pending button label): decorative, aria-hidden, vertically centred on the line. Off = standalone, role="status" with `label`. */
  inline?: boolean;
  /** What a screen reader hears — standalone only (default "Loading"). Say WHICH wait: "Loading payment proof". */
  label?: string;
  /** Extra classes — a `text-*` colour replaces the accent (text-chrome-content over a camera frame); spacing utilities ride beside it. */
  class?: string;
}) {
  const ink = () => (hasInk(props.class) ? '' : 'text-primary');
  return (
    <span
      class={`loading loading-spinner ${SIZE[props.size ?? 's']} ${ink()} ${props.inline ? 'align-middle' : ''} ${props.class ?? ''}`}
      data-size={props.size ?? 's'}
      role={props.inline ? undefined : 'status'}
      aria-label={props.inline ? undefined : (props.label ?? 'Loading')}
      aria-hidden={props.inline ? 'true' : undefined}
    />
  );
}
